import { Paragraph } from '../../Typography/Paragraph/Paragraph'
import React from 'react'
import Icon2 from '../../Icon2'
import { DetectionListItem, TranslationButton, TranslationPopoverProps, DetectionItem } from '../helper'
import Field from '../../Field/Field'
import { useFormikContext } from 'formik'

interface ExtendedDetectionItem extends DetectionItem {
    description?: Array<Record<string, string>>;
}

interface FieldConfig {
    uid: string;
    title?: string;
    type: string;
    threshold?: boolean;
    threshold_value?: number;
    hidden?: boolean;
    checkbox?: boolean;
    banner?: boolean;
    banner_text?: string;
    highlight?: boolean;
    highlightOnClick?: () => void;
    enable_translation?: boolean;
    TranslationPopover?: (props: TranslationPopoverProps) => React.JSX.Element;
    getCheckboxResults?: (result: ExtendedDetectionItem[]) => void;
    showConfidence?: boolean;
    hotspots?: boolean;
    canvasSize?: number;
    strokeColor?: string;
    translationLabel?: string;
}

interface PartialSelectResult {
    name: string;
    selected: boolean;
}

type UpdateValueFunction = (event: {
    target: {
        value: ExtendedDetectionItem[];
        type: string;
        name: string;
    };
}) => void;

interface ListItemProps {
    field: FieldConfig;
    items: ExtendedDetectionItem[];
    threshold_value?: number;
    updateValue?: UpdateValueFunction;
    mainImage?: string;
    setPartialSelect?: (result: PartialSelectResult) => void;
    highlightedIndex: number | null;
    setHighlightedIndex: (index: number | null) => void;
    isTranslated?: boolean;
    setIsTranslated?: (isTranslated: boolean) => void;
    handleCheckboxResult?: () => void;
}

const ListItem = ({
    field,
    items,
    threshold_value,
    updateValue,
    mainImage,
    setPartialSelect,
    highlightedIndex,
    setHighlightedIndex,
    isTranslated,
    setIsTranslated,
    handleCheckboxResult,
}: ListItemProps) => {
    const { initialValues } = useFormikContext();

    const handleHighlightClick = (index: number) => {
        if (highlightedIndex === index) {
            setHighlightedIndex(null);
        } else {
            setHighlightedIndex(index);
            field.highlightOnClick?.();
        }
    }

    return (
        <div className='Sidebar__list-item__wrapper'>
            {/* Show banner only if threshold is enabled, banner is true, and any item meets threshold */}
            {field.threshold && field.banner && items.some(item => item.confidence >= threshold_value) && (
                <div className='Sidebar__banner'>
                    <div className='Sidebar__banner-icon__container'>
                        <Icon2
                            icon='InformationCircle'
                            version='v2'
                            size='medium'
                        />
                    </div>
                    <Paragraph
                        text={field.banner_text || `This image contains content that exceeds the moderation threshold (${threshold_value}%). The main preview has been blurred.`}
                        variant='p2'
                        variantStyle='regular'
                        className='Sidebar__banner-text'
                    />
                </div>
            )}

            <div className='Sidebar__description-item-wrapper'>
                {/* Render items (already filtered by parent Sidebar component) */}
                {items?.map((item, index) => (
                    <div
                        key={index}
                        className={`Sidebar__description-item-container ${field.highlight ? 'hoverable' : ''} ${(field.highlight && !field.checkbox && highlightedIndex === index && field.highlightOnClick)
                                ? 'Sidebar__description-item-container__highlight'
                                : ''
                            }`}
                        onClick={() => handleHighlightClick(index)}
                    >
                        <ConditionalFieldWrapper condition={field.checkbox}>
                            <DetectionListItem
                                item={item}
                                checkbox={field.checkbox}
                                thresholdValue={threshold_value}
                                threshold={field.threshold}
                                mainImage={mainImage}
                                handleCheckboxResult={handleCheckboxResult}
                                setPartialSelect={setPartialSelect}
                                showConfidence={field.showConfidence}
                                hasHotspots={field.hotspots}
                                canvasSize={field.canvasSize}
                                strokeColor={field.strokeColor || '#FF4400'}
                            />
                        </ConditionalFieldWrapper>

                        {/* Render item descriptions if available */}
                        {item.description?.map((description, descIndex) => (
                            <div key={descIndex} className='Sidebar__description-item'>
                                {Object.keys(description).map((key, keyIndex) => (
                                    <div key={keyIndex} className='Sidebar__description-item-key'>
                                        {key.charAt(0).toUpperCase() + key.slice(1)}: {description[key]}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ))}

                {/* Translation button */}
                {field.enable_translation && (
                    <TranslationButton
                        translationLabel={field.translationLabel || `Translate (${items.length}) to Spanish-Spain`}
                        TranslationPopover={field.TranslationPopover}
                        updateValue={updateValue}
                        masterLanguage={initialValues[field.uid]}
                        uid={field.uid}
                        isTranslated={isTranslated}
                        setIsTranslated={setIsTranslated}
                    />
                )}
            </div>
        </div>
    )
}

const ConditionalFieldWrapper = ({ condition, children }: { condition: boolean, children: React.ReactNode }) => {
    return condition ? <Field>{children}</Field> : <>{children}</>
}

export default ListItem